import { useState, useEffect, useContext } from "react"
import { UserContext } from "../../Contexts/UserContext"
import { getSubscriptionDetails, uploadUserDetails, deleteUserDetails } from "../../Config/config"
import Constants from "../../Constants"
import axios from "axios"
import { RaiseRequestContext } from "../../Contexts/RaiseRequestContext"
import Loader from "../Loader"
import { Plus, Edit2, Trash2, Check, X } from 'lucide-react'

export default function SubscriptionPages() {
  const [subscriptionData, setSubscriptionData] = useState([])
  const { getToken, logout } = useContext(UserContext)
  const { setOpen, openRequestPopup } = useContext(RaiseRequestContext)
  const [allowedModulesList, setAllowedModulesList] = useState([])
  const [pointOfContact, setPointOfContact] = useState([])
  
  // New state for subscribers management
  const [subscribers, setSubscribers] = useState([])
  const [newSubscriberId, setNewSubscriberId] = useState("")
  const [newSubscriberName, setNewSubscriberName] = useState("") // Optional name field
  const [editingIndex, setEditingIndex] = useState(-1)
  const [editingValue, setEditingValue] = useState("")
  const [editingName, setEditingName] = useState("") // For editing name
  const [error, setError] = useState("")
  const [isLoading, setIsLoading] = useState(false) // Loading state for API calls
  const [showAllUsers, setShowAllUsers] = useState(false) // State for showing all users from users_list
  
  const _ = require("lodash")

  useEffect(() => {
    axios(`${getSubscriptionDetails}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: getToken(),
      },
    })
      .then((response) => {
        if (response?.data?.status == 200) {
          console.log(response, "user profile response")
          setSubscriptionData(response?.data)
          setPointOfContact(response?.data?.point_of_contact)
          // Load existing subscribers if they exist in the response
          if (response?.data?.subscribers) {
            setSubscribers(response.data.subscribers)
          }
        }
      })
      .catch((error) => {
        if (error?.response?.status == Constants.sessionTerminatedStatus) {
          logout()
        }
        console.log("API not working", error)
      })
  }, [])

  useEffect(() => {
    const allowedModulesStr = localStorage.getItem(Constants.localStorageKeys.allowedModules)
    setAllowedModulesList(JSON.parse(allowedModulesStr) ?? [])
  }, [])

  // Functions for subscribers management
  const getDomain = (email) => {
    const match = email.match(/@(.+)$/)
    return match ? match[1].toLowerCase() : null
  }

  const getContactEmailDomain = () => {
    let emails = [];
    pointOfContact?.forEach((contact) => {
      const contactType = Object.keys(contact)[0];
      const contactData = contact[contactType];
      contactData?.forEach((group) => {
        group?.forEach((item) => {
          if (item.key === "email" && item.value) {
            emails.push(item.value);
          }
        });
      });
    });
    return emails.length > 0 ? getDomain(emails[0]) : null;
  };

  // Helper functions for users_list display
  const getUsersToDisplay = () => {
    const usersList = subscriptionData?.users_list || [];
    if (showAllUsers || usersList.length <= 5) {
      return usersList;
    }
    return usersList.slice(0, 5);
  };

  const getRemainingUsersCount = () => {
    const usersList = subscriptionData?.users_list || [];
    return Math.max(0, usersList.length - 5);
  };

  const deleteUserFromList = async (userEmail) => {
    setIsLoading(true);
    setError("");
    
    try {
      const payload = { email: userEmail };

      const response = await axios.post(deleteUserDetails, payload, {
        headers: {
          "Content-Type": "application/json",
          Authorization: getToken(),
        },
      });

      if (response?.data?.status === 200) {
        // Immediately update local state by removing the deleted user
        const updatedSubscriptionData = {
          ...subscriptionData,
          users_list: subscriptionData.users_list.filter(user => user.email !== userEmail)
        };
        setSubscriptionData(updatedSubscriptionData);
        
        // Also update the subscribers state to keep it in sync
        setSubscribers(prev => prev.filter(subscriber => subscriber.email !== userEmail));
        
        // If after deletion, the remaining users are 5 or less, hide the "show more" section
        const updatedUsersList = updatedSubscriptionData.users_list || [];
        if (updatedUsersList.length <= 5) {
          setShowAllUsers(false);
        }
        
        setError("");
        
        // Optional: Refresh data in background to ensure consistency
        axios.post(getSubscriptionDetails, {}, {
          headers: {
            "Content-Type": "application/json",
            Authorization: getToken(),
          },
        }).then((refreshResponse) => {
          if (refreshResponse?.data?.status === 200) {
            setSubscriptionData(refreshResponse.data);
            setPointOfContact(refreshResponse?.data?.point_of_contact);
          }
        }).catch((refreshError) => {
          console.log("Background refresh failed:", refreshError);
          // Don't show error to user since the main operation succeeded
        });
      } else if (response?.data?.message === "User deleted successfully") {
        // Handle case where status is not 200 but message indicates success
        const updatedSubscriptionData = {
          ...subscriptionData,
          users_list: subscriptionData.users_list.filter(user => user.email !== userEmail)
        };
        setSubscriptionData(updatedSubscriptionData);
        
        // Also update the subscribers state to keep it in sync
        setSubscribers(prev => prev.filter(subscriber => subscriber.email !== userEmail));
        
        // If after deletion, the remaining users are 5 or less, hide the "show more" section
        const updatedUsersList = updatedSubscriptionData.users_list || [];
        if (updatedUsersList.length <= 5) {
          setShowAllUsers(false);
        }
        
        setError("");
      } else {
        setError(response?.data?.message || "Failed to delete user");
      }
    } catch (error) {
      console.error("Error deleting user:", error);
      if (error?.response?.status === Constants.sessionTerminatedStatus) {
        logout();
      }
      setError(error?.response?.data?.message || "Failed to delete user. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const addSubscriber = async () => {
    setError("")
    setIsLoading(true)
    
    if (!newSubscriberName.trim()) {
      setError("Please enter a subscriber name")
      setIsLoading(false)
      return
    }
    
    if (!newSubscriberId.trim()) {
      setError("Please enter a subscriber email")
      setIsLoading(false)
      return
    }

    // Validation checks
    const contactEmailDomain = getContactEmailDomain();
    const newDomain = getDomain(newSubscriberId.trim());
    
    if (contactEmailDomain && newDomain !== contactEmailDomain) {
      setError(`It must match your domain (${contactEmailDomain}).Otherwise please reach out to subscription team for user additions.`);
      setIsLoading(false)
      return;
    }

    if (subscribers.length >= 5) {
      setError("Maximum 5 subscribers allowed")
      setIsLoading(false)
      return
    }

    // Check against both subscribers array and users_list for existing emails
    const emailExistsInSubscribers = subscribers.some(sub => sub.email === newSubscriberId.trim());
    const emailExistsInUsersList = subscriptionData?.users_list?.some(user => user.email === newSubscriberId.trim());
    
    if (emailExistsInSubscribers || emailExistsInUsersList) {
      setError("This subscriber email already exists")
      setIsLoading(false)
      return
    }

    // Enforce same domain
    if (subscribers.length > 0) {
      const existingDomain = getDomain(subscribers[0].email)
      if (!existingDomain || !newDomain || existingDomain !== newDomain) {
        setError(`All subscribers must have the same domain (${existingDomain ? existingDomain : "unknown"})`)
        setIsLoading(false)
        return
      }
    }

    try {
      // Prepare payload
      const payload = {
        email: newSubscriberId.trim(),
        name: newSubscriberName.trim()
      }

      // Call API
      const response = await axios.post(uploadUserDetails, payload, {
        headers: {
          "Content-Type": "application/json",
          Authorization: getToken(),
        },
      })

      if (response?.data?.status === 200) {
        // Immediately update local state by adding the new user
        const newUser = {
          email: newSubscriberId.trim(),
          name: newSubscriberName.trim()
        };
        
        const updatedSubscriptionData = {
          ...subscriptionData,
          users_list: [...(subscriptionData.users_list || []), newUser]
        };
        setSubscriptionData(updatedSubscriptionData);
        
        // Clear form inputs immediately
        setNewSubscriberId("");
        setNewSubscriberName("");
        setError("");
        
        // Also update local subscribers state for backward compatibility
        const newSubscriber = {
          email: newSubscriberId.trim(),
          name: newSubscriberName.trim()
        }
        setSubscribers([...subscribers, newSubscriber]);
        
        // Optional: Refresh data in background to ensure consistency
        axios.post(getSubscriptionDetails, {}, {
          headers: {
            "Content-Type": "application/json",
            Authorization: getToken(),
          },
        }).then((refreshResponse) => {
          if (refreshResponse?.data?.status === 200) {
            setSubscriptionData(refreshResponse.data);
            setPointOfContact(refreshResponse?.data?.point_of_contact);
          }
        }).catch((refreshError) => {
          console.log("Background refresh failed:", refreshError);
          // Don't show error to user since the main operation succeeded
        });
      } else if (response?.data?.message && response?.data?.message.includes("successfully")) {
        // Handle case where status is not 200 but message indicates success
        const newUser = {
          email: newSubscriberId.trim(),
          name: newSubscriberName.trim()
        };
        
        const updatedSubscriptionData = {
          ...subscriptionData,
          users_list: [...(subscriptionData.users_list || []), newUser]
        };
        setSubscriptionData(updatedSubscriptionData);
        
        // Clear form inputs immediately
        setNewSubscriberId("");
        setNewSubscriberName("");
        setError("");
        
        // Also update local subscribers state for backward compatibility
        const newSubscriber = {
          email: newSubscriberId.trim(),
          name: newSubscriberName.trim()
        }
        setSubscribers([...subscribers, newSubscriber]);
      } else {
        setError(response?.data?.message || "Failed to add subscriber")
      }
    } catch (error) {
      console.error("Error adding subscriber:", error)
      if (error?.response?.status === Constants.sessionTerminatedStatus) {
        logout()
      }
      setError(error?.response?.data?.message || "Failed to add subscriber. Please try again.")
    } finally {
      setIsLoading(false)
    }
  }

  const deleteSubscriber = async (index) => {
    setIsLoading(true)
    setError("")
    
    const subscriberToDelete = subscribers[index]
    
    try {
      // Prepare payload
      const payload = {
        email: subscriberToDelete.email
      }

      // Call API
      const response = await axios.post(deleteUserDetails, payload, {
        headers: {
          "Content-Type": "application/json",
          Authorization: getToken(),
        },
      })

      if (response?.data?.status === 200) {
        // API call successful, update local state
        const updatedSubscribers = subscribers.filter((_, i) => i !== index)
        setSubscribers(updatedSubscribers)
        setError("")
      } else {
        setError(response?.data?.message || "Failed to delete subscriber")
      }
    } catch (error) {
      console.error("Error deleting subscriber:", error)
      if (error?.response?.status === Constants.sessionTerminatedStatus) {
        logout()
      }
      setError(error?.response?.data?.message || "Failed to delete subscriber. Please try again.")
    } finally {
      setIsLoading(false)
    }
  }

  const startEditing = (index) => {
    setEditingIndex(index)
    setEditingValue(subscribers[index].email)
    setEditingName(subscribers[index].name || "")
    setError("")
  }

  const saveEdit = async () => {
    setError("");
    setIsLoading(true)
    
    if (!editingName.trim()) {
      setError("Please enter a name");
      setIsLoading(false)
      return;
    }
    
    if (!editingValue.trim()) {
      setError("Please enter a subscriber email");
      setIsLoading(false)
      return;
    }

    if (subscribers.some((sub, i) => sub.email === editingValue.trim() && i !== editingIndex)) {
      setError("This subscriber email already exists");
      setIsLoading(false)
      return;
    }

    // Domain validation
    const contactEmailDomain = getContactEmailDomain();
    const newDomain = getDomain(editingValue.trim());
    
    if (contactEmailDomain && newDomain !== contactEmailDomain) {
      setError(`Subscriber email domain must match contact domain (${contactEmailDomain})`);
      setIsLoading(false)
      return;
    }

    // Enforce same domain with other subscribers
    if (subscribers.length > 0) {
      const otherSubscribers = subscribers.filter((_, i) => i !== editingIndex);
      if (otherSubscribers.length > 0) {
        const existingDomain = getDomain(otherSubscribers[0].email);
        if (!existingDomain || !newDomain || existingDomain !== newDomain) {
          setError(`All subscribers must have the same domain (${existingDomain ? existingDomain : "unknown"})`);
          setIsLoading(false)
          return;
        }
      }
    }

    try {
      const oldSubscriber = subscribers[editingIndex]
      
      // First delete the old subscriber
      await axios.post(deleteUserDetails, { email: oldSubscriber.email }, {
        headers: {
          "Content-Type": "application/json",
          Authorization: getToken(),
        },
      })

      // Then add the updated subscriber
      const payload = {
        email: editingValue.trim(),
        name: editingName.trim()
      }

      const response = await axios.post(uploadUserDetails, payload, {
        headers: {
          "Content-Type": "application/json",
          Authorization: getToken(),
        },
      })

      if (response?.data?.status === 200) {
        // API calls successful, update local state
        const updatedSubscribers = [...subscribers];
        updatedSubscribers[editingIndex] = {
          email: editingValue.trim(),
          name: editingName.trim()
        };
        setSubscribers(updatedSubscribers);
        setEditingIndex(-1);
        setEditingValue("");
        setEditingName("");
        setError("")
      } else {
        setError(response?.data?.message || "Failed to update subscriber")
      }
    } catch (error) {
      console.error("Error updating subscriber:", error)
      if (error?.response?.status === Constants.sessionTerminatedStatus) {
        logout()
      }
      setError(error?.response?.data?.message || "Failed to update subscriber. Please try again.")
    } finally {
      setIsLoading(false)
    }
  }

  const cancelEdit = () => {
    setEditingIndex(-1)
    setEditingValue("")
    setEditingName("")
    setError("")
  }

  const handleKeyPress = (e, action) => {
    if (e.key === "Enter") {
      if (action === "add") {
        addSubscriber()
      } else if (action === "edit") {
        saveEdit()
      }
    }
  }

  const filteredPointOfContact = pointOfContact
    ?.map((contact, index) => {
      const contactType = Object.keys(contact)[0]
      const contactData = contact[contactType]
      if (contactType === "cfs") {
        const peData = pointOfContact.find((item) => Object.keys(item)[0] === "pe")?.pe || []
        return {
          [contactType]: contactData
            .map((group) =>
              group.filter(
                (item) =>
                  !peData.some((peGroup) =>
                    peGroup.some((peItem) => peItem.label === item.label && peItem.value === item.value),
                  ),
              ),
            )
            .filter((group) => group.length > 0),
        }
      }
      return contact
    })
    .filter((contact) => {
      const contactType = Object.keys(contact)[0]
      return contact[contactType].length > 0
    })

  return (
    <div className="lg:mt-5">
      <div className="bg-white shadow rounded-[4px] ">
        <div className="grid grid-cols-12 py-3 px-4 border-b border-[#E3E3E3]">
          <div className=" col-span-2">
            <p className="  font-sans_book_body text-[#666666] text-[14px] tracking-[-0.3px] leading-[17px]">Name</p>
          </div>
          <div className="col-span-5 ">
            <p className="whitespace-nowrap  font-sans_book_body  text-[#666666] text-[14px] tracking-[-0.3px] leading-[17px]">
              <span className="text-[#333333] font-sans_medium_body">{subscriptionData?.user?.name}</span>
            </p>
          </div>
        </div>
        
        <div className="lg:grid lg:grid-cols-12 px-4 pt-4 space-y-3 lg:space-y-0 border-b border-[#E3E3E3]">
          <div className="lg:col-span-2">
            <p className="font-sans_book_body text-[#666666] text-[14px] tracking-[-0.3px] leading-[17px] whitespace-nowrap">
              Your Plan
            </p>
          </div>
          <div className="lg:col-span-6">
            <div className="">
              <p className="  font-sans_book_body  text-[#333333] text-[14px] tracking-[-0.3px] leading-[17px]">
                Databases included in Subscription
              </p>
              <div className="py-2 space-x-4">
                {subscriptionData?.primary_type?.map((type, index) => (
                  <span
                    key={index}
                    className="px-3 py-0.5 font-sans_medium_body border border-[#CCCCCC] inline-flex items-center rounded-xl text-[#333333] leading-[16px] text-[13px] uppercase bg-[#F1CA75]"
                  >
                    {type}
                  </span>
                ))}

                {subscriptionData?.user?.has_newsletter_permission && (
                  <span
                    className="px-3 py-0.5 font-sans_medium_body border border-[#CCCCCC] inline-flex items-center rounded-xl text-[#333333] leading-[16px] text-[13px]  bg-[#F1CA75]"
                  >
                    Newsletter
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="lg:col-span-4">
            <div className="lg:mt-6 lg:text-right text-center">
              <span className="font-SequelSans text-[#BC8B20] text-[14px] leading-[17px] tracking-[-0.3px] ">
                <span className="text-[#666666] text-[14px] leading-[17px]">Upgrade Subscription, </span>
                <span
                  className="cursor-pointer"
                  onClick={() => {
                    setOpen(true)
                    openRequestPopup("subscription", "sales")
                  }}
                >
                  Raise Request
                </span>{" "}
              </span>
            </div>
          </div>
        </div>

        {allowedModulesList.includes("cfs") && (
          <div className="grid grid-cols-12 py-3 px-4 border-b border-[#E3E3E3]">
            <div className=" col-span-2">
              <p className="  font-sans_book_body text-[#666666] text-[14px] tracking-[-0.3px] leading-[17px]">
                Custom Request
              </p>
            </div>
            <div className="col-span-5">
              <p className="whitespace-nowrap  font-sans_book_body  text-[#666666] text-[14px] tracking-[-0.3px] leading-[17px]">
                Request left{" "}
                <span className="text-[#333333] font-sans_medium_body">
                  {subscriptionData?.request_left ? subscriptionData?.request_left : 0} /{" "}
                  {subscriptionData?.request_limit ? subscriptionData?.request_limit : 0}.
                </span>{" "}
                Do you want to increase the request limit?
              </p>
            </div>
            <div className=" col-span-5">
              <div className="text-right">
                <span
                  className=" font-sans_book_body text-[#BC8B20] text-[14px] leading-[17px] tracking-[-0.3px] cursor-pointer"
                  onClick={() => {
                    setOpen(true)
                    openRequestPopup("subscription", "sales")
                  }}
                >
                  Raise Request
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Updated Maintain Subscribers Section - Now showing users_list */}
        {subscriptionData?.user?.is_poc && (

        <div className="grid grid-cols-12 py-3 px-4 border-b border-[#E3E3E3]">
          <div className="col-span-2">
            <p className="font-sans_book_body text-[#666666] text-[14px] tracking-[-0.3px] leading-[17px]">
              Add / update User list
            </p>
          </div>
          <div className="col-span-10">
            {/* Input section */}
            <div className="space-y-3 mb-4">
              <div className="flex items-center gap-2">
                <div style={{width: '20%'}}>
                  <input
                    type="text"
                    value={newSubscriberName}
                    onChange={(e) => setNewSubscriberName(e.target.value)}
                    placeholder="Enter name *"
                    className="w-full px-3 py-2 border border-[#CCCCCC] rounded-md text-[14px] focus:outline-none focus:ring-2 focus:ring-[#BC8B20] focus:border-transparent"
                    disabled={subscribers.length >= 5 || isLoading}
                  />
                </div>
                <div style={{width: '32%'}}>
                  <input
                    type="email"
                    value={newSubscriberId}
                    onChange={(e) => setNewSubscriberId(e.target.value)}
                    onKeyPress={(e) => handleKeyPress(e, "add")}
                    placeholder="Enter subscriber email *"
                    className="w-full px-3 py-2 border border-[#CCCCCC] rounded-md text-[14px] focus:outline-none focus:ring-2 focus:ring-[#BC8B20] focus:border-transparent"
                    disabled={subscribers.length >= 5 || isLoading}
                  />
                </div>
                <button
                  onClick={addSubscriber}
                  disabled={subscribers.length >= 5 || isLoading}
                  className="p-2 bg-[#BC8B20] text-white rounded-md hover:bg-[#A67A1C] disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
                >
                  {isLoading ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    <Plus size={16} />
                  )}
                </button>
              </div>
            </div>

            {/* Error message */}
            {error && <div className="text-red-500 text-[12px] mb-3">{error}</div>}

            {/* Loading indicator */}
            {isLoading && (
              <div className="text-[#BC8B20] text-[12px] mb-3 flex items-center gap-2">
                <div className="w-3 h-3 border-2 border-[#BC8B20] border-t-transparent rounded-full animate-spin"></div>
                Processing...
              </div>
            )}

            {/* Users List Display */}
            {subscriptionData?.users_list && subscriptionData.users_list.length > 0 && (
              <div className="space-y-2">
                <p className="font-sans_medium_body text-[#333333] text-[13px] mb-2">
                  Current Users ({subscriptionData.users_list.length}):
                </p>
                
                {/* Users Grid */}
                <div className="flex flex-col gap-2">
                  {/* Display users in rows of 2, with left alignment for odd numbers */}
                  {Array.from({ length: Math.ceil(getUsersToDisplay().length / 2) }, (_, rowIndex) => (
                    <div key={rowIndex} className="flex gap-2 justify-start">
                      {getUsersToDisplay().slice(rowIndex * 2, (rowIndex + 1) * 2).map((user, index) => (
                        <div key={user.email} className="w-[48%] max-w-[240px] min-w-[180px] flex items-center justify-between bg-gray-50 px-2 py-1.5 rounded-md">
                          <div className="flex flex-col flex-1 min-w-0">
                            <span className="font-sans_book_body text-[#333333] text-[12px] truncate">{user.name}</span>
                            <span className="font-sans_book_body text-[#666666] text-[10px] truncate">{user.email}</span>
                          </div>
                          <div className="flex items-center gap-1 ml-1">
                            <button
                              onClick={() => deleteUserFromList(user.email)}
                              className="p-1 text-red-600 hover:bg-red-100 rounded transition-colors flex-shrink-0"
                              disabled={isLoading}
                              title="Delete user"
                            >
                              <Trash2 size={12} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>

                {/* Show More/Less Button */}
                {subscriptionData.users_list.length > 5 && (
                  <div className="flex justify-center mt-3">
                    <button
                      onClick={() => setShowAllUsers(!showAllUsers)}
                      className="px-4 py-2 text-[#BC8B20] border border-[#BC8B20] rounded-md hover:bg-[#BC8B20] hover:text-white transition-colors text-[13px]"
                    >
                      {showAllUsers ? (
                        <>Show Less</>
                      ) : (
                        <>Show {getRemainingUsersCount()} More</>
                      )}
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Legacy Subscribers Section - keeping for backward compatibility */}
           
          </div>
        </div>
        )}
      </div>

      {/* Point of Contact Section - keeping the existing implementation */}
      {pointOfContact?.length > 0 && (
        <div className="bg-white shadow p-6">
          <p className="text-[16px] text-[#2B2A29] leading-[19px] tracking-[-0.34px] font-sans_medium_body">
            Point of Contact (s)
          </p>
          <dl className="space-y-10 md:space-y-0 overflow-x-auto scrollbar-remove">
            <div className="min-w-full align-middle">
              <div className="lg:w-full md:w-[150%] w-[200%]">
                <div className="overflow-x-auto overflow-y-hidden lg:grid lg:grid-cols-12 w-full flex divide-x mt-2">
                  {allowedModulesList.includes("pe") && allowedModulesList.includes("cfs") ? (
                    // Show both PE and CFS if both modules are allowed
                    pointOfContact?.length > 0 ? (
                      filteredPointOfContact.map((contact, contactIndex) => {
                        const contactType = Object.keys(contact)[0]
                        const contactData = contact[contactType]
                        return (
                          <div
                            key={contactIndex}
                            className={`
                              ${filteredPointOfContact?.length === 2 ? "lg:col-span-6 w-[100%] lg:w-full" : "lg:col-span-12 w-full"}
                              ${contactIndex === 1 ? "pl-10" : ""}
                            `}
                          >
                            <div className="lg:grid lg:grid-cols-6 px-4 py-4 space-y-3 lg:space-y-0 ">
                              <div className="lg:col-span-2">
                                <p className="font-sans_book_body text-[#666666] text-[14px] tracking-[-0.3px] leading-[17px] whitespace-nowrap">
                                  Product
                                </p>
                              </div>
                              <div className=" space-x-4 flex ">
                                {(() => {
                                  const tags = [];
                                  let shouldShowCfs = false;
                                  
                                  // Helper function to check if contact exists in both PE and CFS
                                  const isContactInBothPeAndCfs = () => {
                                    const peData = pointOfContact.find((item) => Object.keys(item)[0] === "pe")?.pe || [];
                                    const cfsData = pointOfContact.find((item) => Object.keys(item)[0] === "cfs")?.cfs || [];
                                    
                                    // Check if there's any overlap in contact data between PE and CFS
                                    return peData.some(peGroup =>
                                      peGroup.some(peItem =>
                                        cfsData.some(cfsGroup =>
                                          cfsGroup.some(cfsItem =>
                                            peItem.label === cfsItem.label && 
                                            peItem.value === cfsItem.value
                                          )
                                        )
                                      )
                                    );
                                  };

                                  const hasContactInBoth = isContactInBothPeAndCfs();

                                  // First pass: Add all non-CFS tags
                                  subscriptionData?.primary_type?.forEach((type, typeIndex) => {
                                    if (
                                      contactType === "pe" &&
                                      (type === "pe" || type === "vc" || type === "re" || type === "m&a")
                                    ) {
                                      tags.push(
                                        <span
                                          key={`pe-${typeIndex}`}
                                          className="px-3 py-0.5 font-sans_medium_body border border-[#CCCCCC] inline-flex items-center rounded-xl text-[#333333] leading-[16px] text-[13px] uppercase bg-[#F1CA75]"
                                        >
                                          {type}
                                        </span>
                                      );
                                      
                                      // Check if we should show CFS (but don't add it yet)
                                      if (hasContactInBoth && subscriptionData?.primary_type?.includes("cfs")) {
                                        shouldShowCfs = true;
                                      }
                                    }
                                    
                                    if (contactType === "cfs" && type === "cfs") {
                                      shouldShowCfs = true;
                                    }
                                  });

                                  // Second pass: Add CFS at the end if needed
                                  if (shouldShowCfs) {
                                    tags.push(
                                      <span
                                        key="cfs-last"
                                        className="px-3 py-0.5 font-sans_medium_body border border-[#CCCCCC] inline-flex items-center rounded-xl text-[#333333] leading-[16px] text-[13px] uppercase bg-[#F1CA75]"
                                      >
                                        cfs
                                      </span>
                                    );
                                  }

                                  return tags;
                                })()}
                              </div>
                            </div>
                            {console.log(contactData," contactGroup")}
                            {/* Render Groups */}
                            {contactData.map((contactGroup, groupIndex) => (
                              <div
                                key={groupIndex}
                                className={`max-h-[18rem] min-w-fit ${groupIndex > 0 ? "mt-6" : ""}`}
                              >
                                <div className="flex flex-col gap-4 bg-gray-50 p-4 rounded-md shadow-sm">
                                  {/* Render Items */}
                                  {contactGroup.map((contactItem, itemIndex) => (
                                    <div key={itemIndex} className="flex py-1 grid grid-cols-3  gap-3 lg:gap-0">
                                      <div className=" font-sans_book_body text-[#666666] text-[14px] tracking-[-0.3px] leading-[17px] whitespace-nowrap">
                                        {contactItem.label}
                                      </div>
                                      <div className=" font-sans_medium_body  text-[#333333] text-[14px] tracking-[-0.3px] leading-[17px]">
                                        {contactItem.value}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        )
                      })
                    ) : (
                      <div className="bg-white col-span-12 flex items-center justify-center h-40">
                        <Loader />
                      </div>
                    )
                  ) : allowedModulesList.includes("pe") ? (
                    // Show only PE if PE is allowed but CFS is not
                    pointOfContact?.length > 0 ? (
                      filteredPointOfContact
                        .filter((contact) => Object.keys(contact)[0] === "pe")
                        .map((contact, contactIndex) => {
                          const contactType = Object.keys(contact)[0]
                          const contactData = contact[contactType]
                          return (
                            <div
                              key={contactIndex}
                              className={`
                                ${filteredPointOfContact?.length === 2 ? "lg:col-span-6 w-[100%] lg:w-full" : "lg:col-span-12 w-full"}
                                ${contactIndex === 1 ? "pl-10" : ""}
                              `}
                            >
                              {/* Render Groups */}
                              {contactData.map((contactGroup, groupIndex) => (
                                <div
                                  key={groupIndex}
                                  className={`max-h-[18rem] min-w-fit ${groupIndex > 0 ? "mt-6" : ""}`}
                                >
                                  <div className="flex flex-col gap-4 bg-gray-50 p-4 rounded-md shadow-sm">
                                    {/* Render Items */}
                                    {contactGroup.map((contactItem, itemIndex) => (
                                      <div key={itemIndex} className="flex py-1 grid grid-cols-3  gap-3 lg:gap-0">
                                        <div className="font-sans_book_body text-[#666666] text-[14px] tracking-[-0.3px] leading-[17px]">
                                          {contactItem.label}:
                                        </div>
                                        <div className="font-sans_medium_body text-[#333333] text-[14px] tracking-[-0.3px] leading-[17px]">
                                          {contactItem.value}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )
                        })
                    ) : (
                      <div className="bg-white col-span-12 flex items-center justify-center h-40">
                        <Loader />
                      </div>
                    )
                  ) : allowedModulesList.includes("cfs") ? (
                    // Show only CFS if CFS is allowed but PE is not
                    pointOfContact?.length > 0 ? (
                      filteredPointOfContact
                        .filter((contact) => Object.keys(contact)[0] === "cfs")
                        .map((contact, contactIndex) => {
                          const contactType = Object.keys(contact)[0]
                          const contactData = contact[contactType]
                          return (
                            <div
                              key={contactIndex}
                              className={`
                                ${filteredPointOfContact?.length === 2 ? "lg:col-span-6 w-[100%] lg:w-full" : "lg:col-span-12 w-full"}
                                ${contactIndex === 1 ? "pl-10" : ""}
                              `}
                            >
                              {/* Render Groups */}
                              {contactData.map((contactGroup, groupIndex) => (
                                <div
                                  key={groupIndex}
                                  className={`max-h-[18rem] min-w-fit ${groupIndex > 0 ? "mt-6" : ""}`}
                                >
                                  <div className="flex flex-col gap-4 bg-gray-50 p-4 rounded-md shadow-sm">
                                    {/* Render Items */}
                                    {contactGroup.map((contactItem, itemIndex) => (
                                      <div key={itemIndex} className="flex py-1 grid grid-cols-6  gap-3 lg:gap-0">
                                        <div className="font-sans_book_body text-[#666666] text-[14px] tracking-[-0.3px] leading-[17px]">
                                          {contactItem.label}:
                                        </div>
                                        <div className="font-sans_medium_body text-[#333333] text-[14px] tracking-[-0.3px] leading-[17px]">
                                          {contactItem.value}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )
                        })
                    ) : (
                      <div className="bg-white col-span-12 flex items-center justify-center h-40">
                        <Loader />
                      </div>
                    )
                  ) : null}
                </div>
              </div>
            </div>
          </dl>
        </div>
      )}
    </div>
  )
}
